import React, { useState, useEffect, useCallback } from "react";
import ProgressBar from "./components/ProgressBar";
import Timer from "./components/Timer";
import Celebration from "./components/Celebration";
import Level1FindMe from "./levels/Level1FindMe";
import Level2OddOne from "./levels/Level2OddOne";
import Level3Memory from "./levels/Level3Memory";
import Level4Matching from "./levels/Level4Matching";
import Level5ThinkWhy from "./levels/Level5ThinkWhy";
import CompanionSetup from "./components/CompanionSetup";
import CompanionTurnReveal from "./components/CompanionTurnReveal";
import CompanionPanel from "./components/CompanionPanel";
import CompanionPlaying from "./components/CompanionPlaying";
import { simulateCompanionFullGame, estimateScore, emojiForCompanion } from "./utils/companionPlay";

// dear Claudeie when you were on leave till 9.10 PM... I did tested the new companion game ... it went through well... and kid's turn came... but i got disappointed as I am not able to any button or panel or icon to click and see Mom's score of every level and time taken... so that the kid can have an info and inspiration... also if you think so ... show <sec> left to beat <mummy> ... here beating mummy is not question or level by level.. its overall..

const LEVEL_COMPONENTS = {
  1: Level1FindMe,
  2: Level2OddOne,
  3: Level3Memory,
  4: Level4Matching,
  5: Level5ThinkWhy,
};

export default function TinyThinkGame({
  theme, currentLevel, phase, questions, loadingQ, qError, todayCompleted,
  lastLevelResult, surpriseBox, sessionElapsedSec, showTimeNudge, dismissTimeNudge,
  submitLevelResult, fetchQuestions, goToNextLevel, returnHome, sound,
}) {
  const [levelScoresToday, setLevelScoresToday] = useState([0, 0, 0, 0, 0]);
  const [companionStage, setCompanionStage] = useState("setup"); // "setup" | "reveal" | "kidTurn"
  const [companionName, setCompanionName] = useState("Mom");
  const [companionResults, setCompanionResults] = useState([]);
  const [companionLevel, setCompanionLevel] = useState(1)
  const [levelTimesToday, setLevelTimesToday] = useState([0, 0, 0, 0, 0]);

  const handleLevelComplete = useCallback(
    async (result) => {
      const res = await submitLevelResult({ level: currentLevel, ...result });
      setLevelScoresToday((prev) => {
        const next = [...prev];
        next[currentLevel - 1] = res.score;
        return next;
      });
      setLevelTimesToday((prev) => {              
        const next = [...prev];
        next[currentLevel - 1] = result.timeTakenSec;
        return next;
      });
      sound.levelComplete();
      if (surpriseBox) sound.surpriseBox();
    },
    [currentLevel, submitLevelResult, sound, surpriseBox]
  );

  const handleCompanionLevelComplete = useCallback(
      async (result) => {
        const score = estimateScore(result);
        setCompanionResults((prev) => [...prev, { ...result, score }]);

        if (companionLevel < 5) {
          const next = companionLevel + 1;
          setCompanionLevel(next);
          await fetchQuestions(next, theme);
        } else {
          await fetchQuestions(1, theme);
          setCompanionStage("reveal");
        }
      },
      [companionLevel, theme, fetchQuestions]
    );
    
  const handleContinue = () => {
    goToNextLevel();
  };

  const LevelComponent = LEVEL_COMPONENTS[currentLevel];
  const KidLevelComponent = LEVEL_COMPONENTS[currentLevel];
  const CompanionLevelComponent = LEVEL_COMPONENTS[companionLevel];

  return (
    <div className="min-h-screen bg-[#240a74] text-white flex flex-col">
      <header className="px-4 pt-4 pb-2 flex items-center justify-between max-w-md mx-auto lg:max-w-lg w-full">
        <button onClick={returnHome} className="text-lg w-fit px-2 py-1 text-white font-semibold flex items-center gap-1.5" aria-label="Back home">
          <i className="fa-solid fa-chevron-left text-sm w-fit px-2 py-1" aria-hidden="true"></i> Home
        </button>
        <Timer elapsedSec={sessionElapsedSec} showNudge={showTimeNudge} onDismissNudge={dismissTimeNudge} />
      </header>

      <ProgressBar currentLevel={currentLevel} levelScores={levelScoresToday} />
      <CompanionPanel                                              
        companionName={companionName}
        companionResults={companionResults}
        currentLevel={currentLevel}
        levelTimesToday={levelTimesToday}
      />
      <main className="flex-1 max-w-md mx-auto sm:max-w-lg w-full">
        {phase === "playing" && companionStage === "setup" && (        
          <CompanionSetup
            onCompanionTurn={(name) => {
              setCompanionName(name);
              const results = simulateCompanionFullGame([5, 5, 5, 5, 5]).map((r) => ({ ...r, score: estimateScore(r) }));
              setCompanionResults(results);
              setCompanionStage("playing");
            }}
            onSkip={() => setCompanionStage("kidTurn")}
          />
        )}

        {phase === "playing" && companionStage === "playing" && (
          <>
            <div className="text-center pt-2 pb-1">
              <p className="text-xs font-bold text-[#FF8B6B] uppercase tracking-wider">
                {emojiForCompanion(companionName)} {companionName}'s Turn — Level {companionLevel}
              </p>
            </div>
            {loadingQ && (
              <div className="flex items-center justify-center py-16 text-[#8B7FA8] text-sm">Loading fun… 🎈</div>
            )}
            {qError && (
              <div className="flex flex-col items-center gap-2 py-16 text-center px-4">
                <p className="text-lg lg:text-xl text-red-500">Couldn't load this level: {qError}</p>
                <button onClick={returnHome} className="text-sm font-semibold text-gray-100 hover:text-white w-fit px-2 py-1">Go back home</button>
              </div>
            )}
            {!loadingQ && !qError && questions?.length > 0 && CompanionLevelComponent && (
              <CompanionLevelComponent
                key={`companion-${companionLevel}`}
                questions={questions}
                sound={sound}
                onComplete={handleCompanionLevelComplete}
              />
            )}
          </>
        )}

        {phase === "playing" && companionStage === "reveal" && (        
          <CompanionTurnReveal
            companionName={companionName}
            results={companionResults}
            onContinue={() => setCompanionStage("kidTurn")}
          />
        )}

        {phase === "playing" && companionStage === "kidTurn" && (        
          <>
            {loadingQ && (
              <div className="flex items-center justify-center py-16 text-[#8B7FA8] text-sm">Loading fun… 🎈</div>
            )}
            {qError && (
              <div className="flex flex-col items-center gap-2 py-16 text-center px-4">
                <p className="text-lg lg:text-xl text-red-500">Couldn't load this level: {qError}</p>
                <button onClick={returnHome} className="text-sm font-semibold text-gray-100 hover:text-white w-fit px-2 py-1">Go back home</button>
              </div>
            )}
            {!loadingQ && !qError && questions?.length > 0 && LevelComponent && (
              <LevelComponent questions={questions} sound={sound} onComplete={handleLevelComplete} />
            )}
          </>
        )}

        {phase === "dailySummary" && (
          <DailySummary levelScores={levelScoresToday} onDone={returnHome} />
        )}
      </main>

      {phase === "levelResult" && lastLevelResult && (
        <Celebration
          stars={lastLevelResult.stars}
          isNewBest={lastLevelResult.isNewRecord}
          surpriseBox={currentLevel === 5 ? surpriseBox : null}
          onContinue={handleContinue}
          continueLabel={currentLevel < 5 ? "Next Level →" : "See Today's Summary"}
        />
      )}
    </div>
  );
}

function DailySummary({ levelScores, onDone }) {
  const total = levelScores.reduce((a, b) => a + b, 0);
  return (
    <div className="flex flex-col items-center gap-4 py-10 px-4 text-center">
      <div className="text-6xl" aria-hidden="true">🎉</div>
      <h2 className="text-xl sm:text-2xl font-extrabold text-yellow-200">All Done for Today!</h2>
      <p className="text-sm text-[#e9e6f0]">You played all 5 levels together. Great teamwork!</p>
      <div className="bg-white rounded-2xl shadow-sm p-4 w-full max-w-xs">
        <p className="text-3xl font-extrabold text-[#7C3AED]">{total} pts</p>
        <p className="text-xs text-blue-600 mt-1">Today's total score</p>
      </div>
      <button
        onClick={onDone}
        className="bg-[#7C3AED] text-white font-bold rounded-2xl w-fit px-2 py-1 text-sm sm:text-lg mt-2"
      >
        Back to Home
      </button>
    </div>
  );
}
